// LearnCore Frontend - 抽认卡学习模式页面

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { ttsService, TTSService } from '../services/ttsService';
import { useStudyTimeTracker } from '../hooks/useStudyTimeTracker';
import { learningTracker } from '../utils/learningEventTracker';
import Loading from '../components/Loading';
import ImageDisplay from '../components/ImageDisplay';
import ClozeRenderer, { hasClozeNotation, extractClozeAnswers } from '../components/ClozeRenderer';
import type { StudySetDetails, FlashcardStudyState, AdaptiveSessionState } from '../types';
import { 
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  BookOpenIcon,
  SpeakerWaveIcon,
  StopIcon
} from '@heroicons/react/24/outline';

export default function FlashcardModePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [studySetDetails, setStudySetDetails] = useState<StudySetDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [studyState, setStudyState] = useState<FlashcardStudyState>({
    currentIndex: 0,
    isFlipped: false,
    showingTerm: true,
    studiedCards: new Set(),
    correctCards: new Set(),
    incorrectCards: new Set()
  });
  
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [adaptiveEnabled, setAdaptiveEnabled] = useState<boolean>(false);
  const [adaptiveSession, setAdaptiveSession] = useState<AdaptiveSessionState | null>(null);
  const [adaptiveCurrentCard, setAdaptiveCurrentCard] = useState<any | null>(null);
  const [adaptiveCompleted, setAdaptiveCompleted] = useState<boolean>(false);
  const [adaptiveHistory, setAdaptiveHistory] = useState<any[]>([]);
  
  // 学习时间追踪
  const timeTracker = useStudyTimeTracker({
    setId: id || '',
    mode: 'flashcard',
    enabled: !!id && progressLoaded && !loading
  });

  useEffect(() => {
    if (id) {
      loadStudySet(id);
      // 开始学习会话追踪
      learningTracker.startStudySession(id);
    }

    // 加载TTS设置
    ttsService.loadSettings();

    // 页面卸载时结束学习会话
    return () => {
      if (id) {
        learningTracker.endStudySession();
      }
    };
  }, [id]);

  // 当学习集加载完成后，加载学习进度（非自适应模式）
  useEffect(() => {
    if (studySetDetails && id && !progressLoaded && !adaptiveEnabled) {
      loadStudyProgress(id);
    }
  }, [studySetDetails, id, progressLoaded, adaptiveEnabled]);

  // 加载用户设置以确定是否启用自适应学习（闪卡）
  useEffect(() => {
    const initAdaptive = async () => {
      if (!id || !studySetDetails) return;
      try {
        const resp = await apiClient.getUserSettings();
        const enabled = !!resp.settings.adaptive?.flashcard;
        setAdaptiveEnabled(enabled);
        if (enabled) {
          // 启动自适应会话
          const start = await apiClient.startAdaptiveFlashcardSession(id);
          setAdaptiveSession(start.session);
          setAdaptiveCurrentCard(start.next.card);
          setAdaptiveCompleted(start.isCompleted);
          setAdaptiveHistory([]);
          setProgressLoaded(true);
        }
      } catch (e) {
        // 获取设置失败则默认为关闭
        setAdaptiveEnabled(false);
      }
    };
    if (studySetDetails && id && !progressLoaded) {
      initAdaptive();
    }
  }, [studySetDetails, id, progressLoaded]);

  useEffect(() => {
    // 监听TTS状态变化
    const checkSpeaking = () => {
      setIsSpeaking(ttsService.isSpeaking());
    };

    const interval = setInterval(checkSpeaking, 100);
    return () => clearInterval(interval);
  }, []);

  const loadStudySet = async (setId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getStudySet(setId);
      setStudySetDetails(response);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('学习集不存在');
      } else if (err.response?.status === 403) {
        setError('无权访问此学习集');
      } else {
        setError(err.response?.data?.error || '获取学习集失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStudyProgress = async (setId: string) => {
    try {
      const response = await apiClient.getStudySession(setId);
      const sessionData = response.session;
      
      setStudyState({
        currentIndex: sessionData.current_index,
        isFlipped: false,
        showingTerm: true,
        studiedCards: new Set(sessionData.studied_cards),
        correctCards: new Set(sessionData.correct_cards),
        incorrectCards: new Set(sessionData.incorrect_cards)
      });
      
      setProgressLoaded(true);
    } catch (err: any) {
      // 如果获取学习进度失败，继续使用默认状态
      console.warn('获取学习进度失败，使用默认状态:', err.response?.data?.error || err.message);
      setProgressLoaded(true);
    }
  };


  const flipCard = () => {
    setStudyState(prev => ({
      ...prev,
      isFlipped: !prev.isFlipped,
      showingTerm: !prev.showingTerm
    }));
    timeTracker.recordInteraction();
  };

  const markCard = (correct: boolean) => {
    if (!studySetDetails) return;
    if (adaptiveEnabled) {
      if (!id || !adaptiveSession || !adaptiveCurrentCard) return;
      apiClient.answerAdaptiveFlashcard(id, {
        session: adaptiveSession,
        cardId: adaptiveCurrentCard.id,
        knewIt: !!correct
      }).then((res) => {
        // 自适应模式答题成功，计入已学习卡片数
        timeTracker.recordInteraction(1);
        setAdaptiveSession(res.session);
        setAdaptiveCompleted(res.isCompleted);
        if (!res.isCompleted && res.next?.card) {
          // 记录历史卡片
          setAdaptiveHistory(prev => adaptiveCurrentCard ? [...prev, adaptiveCurrentCard] : prev);
          setAdaptiveCurrentCard(res.next.card);
          setStudyState(prev => ({ ...prev, isFlipped: false, showingTerm: true }));
        }
      }).catch(err => {
        console.error('自适应作答失败:', err.response?.data?.error || err.message);
      });
      return;
    }
    
    const currentCard = studySetDetails.set.flashcards[studyState.currentIndex];
    
    const newStudiedCards = new Set(studyState.studiedCards).add(currentCard.id);
    const newCorrectCards = new Set(studyState.correctCards);
    const newIncorrectCards = new Set(studyState.incorrectCards);
    
    // 移除之前的标记
    newCorrectCards.delete(currentCard.id);
    newIncorrectCards.delete(currentCard.id);
    
    // 添加新标记
    if (correct) {
      newCorrectCards.add(currentCard.id);
    } else {
      newIncorrectCards.add(currentCard.id);
    }
    
    const newState = {
      ...studyState,
      studiedCards: newStudiedCards,
      correctCards: newCorrectCards,
      incorrectCards: newIncorrectCards
    };
    
    setStudyState(newState);
    
    // 记录学习交互（标记卡片）
    timeTracker.recordInteraction(1);

    // 记录卡片完成事件
    if (id) {
      learningTracker.recordCardCompleted(id, 1);
    }

    // 保存进度（异步，不阻塞UI）
    if (id) {
      apiClient.saveStudySession(id, {
        current_index: newState.currentIndex,
        studied_cards: Array.from(newState.studiedCards),
        correct_cards: Array.from(newState.correctCards),
        incorrect_cards: Array.from(newState.incorrectCards)
      }).catch(err => {
        console.error('保存学习进度失败:', err.response?.data?.error || err.message);
      });
    }
    
    // 自动跳到下一张卡片
    setTimeout(() => {
      nextCard();
    }, 500);
  };

  const nextCard = () => {
    if (!studySetDetails) return;
    if (adaptiveEnabled) {
      if (!id || !adaptiveSession || !adaptiveCurrentCard) return;
      // 在自适应模式下，"下一张" 默认视为已掌握，快速前进
      apiClient.answerAdaptiveFlashcard(id, {
        session: adaptiveSession,
        cardId: adaptiveCurrentCard.id,
        knewIt: true
      }).then((res) => {
        // 自适应模式“下一张”视为掌握，计入已学习卡片数
        timeTracker.recordInteraction(1);
        setAdaptiveSession(res.session);
        setAdaptiveCompleted(res.isCompleted);
        if (!res.isCompleted && res.next?.card) {
          // 记录当前为历史
          setAdaptiveHistory(prev => adaptiveCurrentCard ? [...prev, adaptiveCurrentCard] : prev);
          setAdaptiveCurrentCard(res.next.card);
          setStudyState(prev => ({ ...prev, isFlipped: false, showingTerm: true }));
        }
      }).catch(err => {
        console.error('自适应下一张失败:', err.response?.data?.error || err.message);
      });
      return;
    }
    
    const nextIndex = studyState.currentIndex + 1;
    if (nextIndex >= studySetDetails.set.flashcards.length) {
      // 已经是最后一张卡片
      return;
    }
    
    const newState = {
      ...studyState,
      currentIndex: nextIndex,
      isFlipped: false,
      showingTerm: true
    };
    
    setStudyState(newState);
    
    // 记录学习交互（下一张卡片）
    timeTracker.recordInteraction();
    
    // 保存进度（异步，不阻塞UI）
    if (id) {
      apiClient.saveStudySession(id, {
        current_index: newState.currentIndex,
        studied_cards: Array.from(newState.studiedCards),
        correct_cards: Array.from(newState.correctCards),
        incorrect_cards: Array.from(newState.incorrectCards)
      }).catch(err => {
        console.error('保存学习进度失败:', err.response?.data?.error || err.message);
      });
    }
  };

  const prevCard = () => {
    if (adaptiveEnabled) {
      if (adaptiveHistory.length === 0) return;
      const last = adaptiveHistory[adaptiveHistory.length - 1];
      setAdaptiveHistory(prev => prev.slice(0, prev.length - 1));
      setAdaptiveCurrentCard(last);
      setStudyState(prev => ({ ...prev, isFlipped: false, showingTerm: true }));
      return;
    }
    if (studyState.currentIndex <= 0) return;
    
    const newState = {
      ...studyState,
      currentIndex: studyState.currentIndex - 1,
      isFlipped: false,
      showingTerm: true
    };
    
    setStudyState(newState);
    
    // 记录学习交互（上一张卡片）
    timeTracker.recordInteraction();
    
    // 保存进度（异步，不阻塞UI）
    if (id) {
      apiClient.saveStudySession(id, {
        current_index: newState.currentIndex,
        studied_cards: Array.from(newState.studiedCards),
        correct_cards: Array.from(newState.correctCards),
        incorrect_cards: Array.from(newState.incorrectCards)
      }).catch(err => {
        console.error('保存学习进度失败:', err.response?.data?.error || err.message);
      });
    }
  };

  const resetStudy = async () => {
    setStudyState({
      currentIndex: 0,
      isFlipped: false,
      showingTerm: true,
      studiedCards: new Set(),
      correctCards: new Set(),
      incorrectCards: new Set()
    });
    
    // 重置服务器端的学习进度
    if (id) {
      try {
        await apiClient.resetStudySession(id);
      } catch (err: any) {
        console.error('重置学习进度失败:', err.response?.data?.error || err.message);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.code === 'Space') {
      e.preventDefault();
      flipCard();
    } else if (e.code === 'ArrowLeft') {
      e.preventDefault();
      prevCard();
    } else if (e.code === 'ArrowRight') {
      e.preventDefault();
      nextCard();
    } else if (e.code === 'KeyT') {
      e.preventDefault();
      handleSpeak();
    }
  };

  const handleSpeak = async () => {
    if (!studySetDetails) return;
    
    if (isSpeaking) {
      ttsService.stop();
      return;
    }

    const currentCard = adaptiveEnabled ? adaptiveCurrentCard : studySetDetails.set.flashcards[studyState.currentIndex];
    if (!currentCard) return;
    
    let textToSpeak = studyState.showingTerm ? currentCard.term : currentCard.definition;
    
    // 如果是填空题且显示挖空状态，提取答案进行朗读
    if (studyState.showingTerm && hasClozeNotation(currentCard.term)) {
      textToSpeak = extractClozeAnswers(currentCard.term);
    }
    
    try {
      await ttsService.speak(textToSpeak);
    } catch (error) {
      console.error('TTS播放失败:', error);
    }
  };

  const toggleAdaptive = async () => {
    if (!id || !studySetDetails) return;
    const target = !adaptiveEnabled;
    setAdaptiveEnabled(target);
    if (target) {
      try {
        const start = await apiClient.startAdaptiveFlashcardSession(id);
        setAdaptiveSession(start.session);
        setAdaptiveCurrentCard(start.next.card);
        setAdaptiveCompleted(start.isCompleted);
        setProgressLoaded(true);
      } catch (e) {
        setAdaptiveEnabled(false);
      }
    } else {
      // 关闭自适应，恢复线性进度
      setAdaptiveSession(null);
      setAdaptiveCurrentCard(null);
      setAdaptiveCompleted(false);
      setStudyState({
        currentIndex: 0,
        isFlipped: false,
        showingTerm: true,
        studiedCards: new Set(),
        correctCards: new Set(),
        incorrectCards: new Set()
      });
      setProgressLoaded(false);
      await loadStudyProgress(id);
    }
  };

  if (loading || !progressLoaded) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text={loading ? "加载学习集..." : "加载学习进度..."} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <BookOpenIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {error}
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="btn-primary"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  if (!studySetDetails || studySetDetails.set.flashcards.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <BookOpenIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            此学习集没有卡片
          </h1>
          <Link
            to={`/sets/${id}/view`}
            className="btn-primary"
          >
            查看学习集
          </Link>
        </div>
      </div>
    );
  }

  const { set } = studySetDetails;
  const currentCard = adaptiveEnabled ? adaptiveCurrentCard : set.flashcards[studyState.currentIndex];
  const linearProgress = ((studyState.currentIndex + 1) / set.flashcards.length) * 100;
  const isLastCard = studyState.currentIndex === set.flashcards.length - 1;
  const isStudyComplete = adaptiveEnabled ? adaptiveCompleted : (studyState.studiedCards.size === set.flashcards.length);

  // 自适应模式进度
  const unseenCount = adaptiveEnabled ? (adaptiveSession?.unseen_queue.length ?? 0) : 0;
  const learningCount = adaptiveEnabled ? (adaptiveSession?.learning_bucket.length ?? 0) : 0;
  const total = set.flashcards.length;
  const rawMastered = adaptiveEnabled ? (total - (unseenCount + learningCount)) : 0;
  const adjustedMastered = adaptiveEnabled && !isStudyComplete ? Math.max(0, rawMastered - 1) : rawMastered; // 当前题未答前不计入
  const adaptivePercent = adaptiveEnabled && total > 0 ? Math.round((adjustedMastered / total) * 100) : 0;

  return (
    <div 
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      onKeyDown={handleKeyPress}
      tabIndex={0}
    >
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <Link
            to={`/sets/${id}/view`}
            state={{ from: location.pathname }}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            返回查看页面
          </Link>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">自适应</span>
              <button
                type="button"
                onClick={toggleAdaptive}
                className={`${adaptiveEnabled ? 'bg-primary-600 focus:ring-primary-500' : 'bg-gray-200 focus:ring-gray-500'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2`}
              >
                <span
                  className={`${adaptiveEnabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-300 shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <button
              onClick={resetStudy}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              重新开始
            </button>
          </div>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {set.title}
        </h1>
        
        {/* 进度条 */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            {adaptiveEnabled ? (
              <>
                <span>掌握: {adjustedMastered} / {total}</span>
                <span>{adaptivePercent}% 完成</span>
              </>
            ) : (
              <>
                <span>进度: {studyState.currentIndex + 1} / {set.flashcards.length}</span>
                <span>{Math.round(linearProgress)}% 完成</span>
              </>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${adaptiveEnabled ? adaptivePercent : Math.round(linearProgress)}%` }}
            />
          </div>
        </div>
        
        {/* 统计信息 */}
        {adaptiveEnabled ? (
          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-1">
              <CheckIcon className="h-4 w-4 text-success-600" />
              <span className="text-success-600">{adjustedMastered} 已掌握</span>
            </div>
            <div className="flex items-center space-x-1">
              <XMarkIcon className="h-4 w-4 text-danger-600" />
              <span className="text-danger-600">{learningCount} 重学</span>
            </div>
            <div className="flex items-center space-x-1">
              <EyeIcon className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">{total - adjustedMastered} 仍需学习</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-1">
              <CheckIcon className="h-4 w-4 text-success-600" />
              <span className="text-success-600">{studyState.correctCards.size} 掌握</span>
            </div>
            <div className="flex items-center space-x-1">
              <XMarkIcon className="h-4 w-4 text-danger-600" />
              <span className="text-danger-600">{studyState.incorrectCards.size} 待练习</span>
            </div>
            <div className="flex items-center space-x-1">
              <EyeIcon className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">{studyState.studiedCards.size} 已学习</span>
            </div>
          </div>
        )}
      </div>

      {/* 学习完成页面 */}
      {isStudyComplete ? (
        <div className="text-center py-12">
          <CheckIcon className="h-20 w-20 text-success-600 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            学习完成！
          </h2>
          <p className="text-lg text-gray-600 mb-8">
              您已经学习了所有 {set.flashcards.length} 张卡片
          </p>
          
          <div className="bg-gray-50 rounded-lg p-6 mb-8 max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">学习统计</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-success-600">掌握:</span>
                <span className="font-medium">{studyState.correctCards.size} 张</span>
              </div>
              <div className="flex justify-between">
                <span className="text-danger-600">待练习:</span>
                <span className="font-medium">{studyState.incorrectCards.size} 张</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-900 font-medium">总计:</span>
                <span className="font-bold">{set.flashcards.length} 张</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={resetStudy}
              className="btn-outline"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              再次学习
            </button>
            
            <Link
              to={`/sets/${id}/view`}
              className="btn-primary"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              查看学习集
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* 抽认卡 */}
          <div className="mb-8">
            <div 
              className={`flashcard min-h-64 flex items-center justify-center text-center cursor-pointer ${
                studyState.isFlipped ? 'flipped' : ''
              } relative`}
              onClick={flipCard}
            >
              <div className="w-full">
                {/* <div className="text-sm text-gray-500 mb-2">
                  {studyState.showingTerm ? '术语' : '定义'}
                </div> */}
                
                {/* 显示图片（如果有） */}
                {studyState.showingTerm && currentCard && currentCard.term_image_url && (
                  <div className="mb-4 flex justify-center">
                    <ImageDisplay 
                      imageUrl={currentCard.term_image_url} 
                      alt="术语图片"
                      size="xl"
                    />
                  </div>
                )}
                {!studyState.showingTerm && currentCard && currentCard.definition_image_url && (
                  <div className="mb-4 flex justify-center">
                    <ImageDisplay 
                      imageUrl={currentCard.definition_image_url} 
                      alt="定义图片"
                      size="xl"
                    />
                  </div>
                )}
                
                <div className="text-2xl font-medium text-gray-900">
                  {currentCard && (
                    <ClozeRenderer 
                      content={studyState.showingTerm ? currentCard.term : currentCard.definition}
                      showCloze={studyState.showingTerm && hasClozeNotation(currentCard.term)}
                    />
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-4">
                  点击卡片翻面
                </div>
              </div>
              
              {/* TTS按钮 */}
              {TTSService.isSupported() && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSpeak();
                  }}
                  className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
                    isSpeaking 
                      ? 'bg-danger-100 text-danger-600 hover:bg-danger-200' 
                      : 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                  }`}
                  title={isSpeaking ? '停止朗读 (T)' : '朗读文本 (T)'}
                >
                  {isSpeaking ? (
                    <StopIcon className="h-5 w-5" />
                  ) : (
                    <SpeakerWaveIcon className="h-5 w-5" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* 控制按钮 */}
          <div className="flex justify-center space-x-4 mb-8">
            <button
              onClick={() => prevCard()}
              disabled={adaptiveEnabled ? adaptiveHistory.length === 0 : studyState.currentIndex === 0}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              上一张
            </button>
            
            <button
              onClick={flipCard}
              className="btn-outline"
            >
              翻转卡片
            </button>
            
            <button
              onClick={() => nextCard()}
              disabled={adaptiveEnabled ? adaptiveCompleted || !adaptiveCurrentCard : isLastCard}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一张
              <ArrowRightIcon className="h-4 w-4 ml-2" />
            </button>
          </div>

          {/* 评价按钮 */}
          {studyState.isFlipped && (
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => markCard(false)}
                className="btn-danger px-8"
              >
                <XMarkIcon className="h-5 w-5 mr-2" />
                还需练习
              </button>
              
              <button
                onClick={() => markCard(true)}
                className="bg-success-600 text-white hover:bg-success-700 focus:ring-success-500 btn px-8"
              >
                <CheckIcon className="h-5 w-5 mr-2" />
                已掌握
              </button>
            </div>
          )}

          {/* 键盘提示 */}
          <div className="text-center text-sm text-gray-500 mt-8">
            <p>键盘快捷键: 空格键翻转卡片，左右箭头键切换卡片{TTSService.isSupported() ? '，T键朗读文本' : ''}</p>
          </div>
        </>
      )}
    </div>
  );
}
